import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DocHeader from "@/components/DocHeader";
import { MEETING_TYPES, typeMeta } from "./Meetings";
import {
  ArrowLeft, Loader2, Save, Sparkles, Plus, Trash2, Check, CheckCircle2,
  Users2, Target, CalendarClock, RotateCcw, Printer,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);

export default function MeetingDetail() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [newItem, setNewItem] = useState("");

  const load = useCallback(() => {
    api.get(`/meetings/${meetingId}`).then((r) => {
      const d = r.data;
      d.checklist = (d.checklist || []).map((c) => (c._k ? c : { ...c, _k: uid() }));
      setM(d);
    }).catch(() => navigate("/meetings"));
  }, [meetingId, navigate]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));

  const save = async (extra = {}, silent = false) => {
    setSaving(true);
    try {
      const body = {
        title: m.title, type: m.type, when: m.when, notes: m.notes, status: m.status,
        checklist: m.checklist, attendees: m.attendees, discussed_targets: m.discussed_targets || [],
        ...extra,
      };
      const { data } = await api.put(`/meetings/${meetingId}`, body);
      data.checklist = (data.checklist || []).map((c) => (c._k ? c : { ...c, _k: uid() }));
      setM(data);
      if (!silent) toast.success("Meeting saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const draftAgenda = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/draft-agenda`);
      const items = (data.items || []).map((text) => ({ _k: uid(), text, done: false }));
      setM((p) => ({ ...p, checklist: [...(p.checklist || []), ...items] }));
      toast.success(`${items.length} agenda items added — review and save`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setDrafting(false); }
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    setM((p) => ({ ...p, checklist: [...(p.checklist || []), { _k: uid(), text: newItem.trim(), done: false }] }));
    setNewItem("");
  };
  const toggleItem = (k) => setM((p) => ({ ...p, checklist: p.checklist.map((c) => (c._k === k ? { ...c, done: !c.done } : c)) }));
  const delItem = (k) => setM((p) => ({ ...p, checklist: p.checklist.filter((c) => c._k !== k) }));
  const togglePresent = (uidv) => setM((p) => ({ ...p, attendees: p.attendees.map((a) => (a.user_id === uidv ? { ...a, present: !a.present } : a)) }));
  const toggleTarget = (tid) => setM((p) => {
    const d = new Set(p.discussed_targets || []);
    d.has(tid) ? d.delete(tid) : d.add(tid);
    return { ...p, discussed_targets: Array.from(d) };
  });

  const remove = async () => {
    if (!window.confirm("Delete this meeting?")) return;
    await api.delete(`/meetings/${meetingId}`);
    navigate("/meetings");
  };

  if (!m) return <div className="flex justify-center py-20 text-slate-300"><Loader2 className="animate-spin" /></div>;
  const t = typeMeta(m.type);
  const review = m.pupil_review || {};
  const done = m.status === "done";

  return (
    <div data-testid="meeting-detail" className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap no-print">
        <button data-testid="meeting-back-btn" onClick={() => navigate("/meetings")} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> All meetings
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="meeting-draft-agenda-btn" onClick={draftAgenda} disabled={drafting}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white disabled:opacity-50" style={{ background: "linear-gradient(135deg,#6C8EBF,#4f6f9c)" }}>
            {drafting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft agenda
          </button>
          <button data-testid="meeting-print-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-slate-300 transition">
            <Printer size={16} /> Print
          </button>
          <button data-testid="meeting-done-btn" onClick={() => save({ status: done ? "planned" : "done" })}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] border transition ${done ? "bg-white border-slate-200 text-slate-700" : "bg-emerald-600 border-emerald-600 text-white"}`}>
            {done ? <><RotateCcw size={16} /> Reopen</> : <><CheckCircle2 size={16} /> Mark done</>}
          </button>
          <button data-testid="meeting-save-btn" onClick={() => save()} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold font-['Outfit'] bg-[#4A5D6B] text-white hover:bg-[#3A4A56] disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
        </div>
      </div>

      <div className="print-area">
        <DocHeader printOnly subtitle="Meeting Record" />
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: t.color }}><CalendarClock size={18} /></span>
          <span className="text-sm text-slate-500">{m.student_name}{m.class_name ? ` · ${m.class_name}` : ""}</span>
          {done && <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 ml-auto inline-flex items-center gap-1"><CheckCircle2 size={11} /> Completed</span>}
        </div>
        <input data-testid="meeting-title-edit" value={m.title} onChange={(e) => set("title", e.target.value)}
          className="w-full font-['Outfit'] text-2xl font-bold text-slate-800 outline-none border-b border-transparent focus:border-slate-200 pb-1" />
        <div className="grid grid-cols-2 gap-3 mt-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Type</span>
            <select data-testid="meeting-type-edit" value={m.type} onChange={(e) => set("type", e.target.value)}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white">
              {MEETING_TYPES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">When</span>
            <input type="datetime-local" data-testid="meeting-when-edit" value={(m.when || "").slice(0, 16)} onChange={(e) => set("when", e.target.value)}
              className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-[#6C8EBF] focus:bg-white" />
          </label>
        </div>
      </div>

      {/* Attendees */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mt-4">
        <h3 className="font-['Outfit'] font-bold text-slate-800 flex items-center gap-2 mb-3"><Users2 size={18} className="text-[#6C8EBF]" /> Attendees (colleagues sharing this pupil)</h3>
        <div className="flex flex-wrap gap-2" data-testid="meeting-attendees">
          {(m.attendees || []).map((a) => (
            <button key={a.user_id} data-testid={`attendee-${a.user_id}`} onClick={() => togglePresent(a.user_id)}
              className={`text-sm rounded-full border px-3 py-1.5 transition ${a.present ? "bg-[#81B29A] border-[#81B29A] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {a.present && <Check size={12} className="inline mr-1" />}{a.name}
            </button>
          ))}
          {(m.attendees || []).length === 0 && <p className="text-sm text-slate-400">No colleagues share this pupil's class yet.</p>}
        </div>
      </div>

      {/* Pupil targets + review */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mt-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-['Outfit'] font-bold text-slate-800 flex items-center gap-2"><Target size={18} className="text-[#9A7AA0]" /> Priority targets to discuss</h3>
          {review.flag === "overdue" && <span className="text-[11px] font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5">Review overdue</span>}
          {review.flag === "due_soon" && <span className="text-[11px] font-bold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">Review due soon</span>}
        </div>
        {(m.pupil_targets || []).length === 0 ? (
          <p className="text-sm text-slate-400">No priority targets set for this pupil.</p>
        ) : (
          <div className="space-y-2" data-testid="meeting-targets">
            {m.pupil_targets.map((tg) => {
              const on = (m.discussed_targets || []).includes(tg.id);
              return (
                <button key={tg.id} data-testid={`meeting-target-${tg.id}`} onClick={() => toggleTarget(tg.id)}
                  className="w-full text-left flex items-start gap-3 rounded-xl border border-slate-100 p-3 hover:border-slate-200 transition">
                  <span className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-[#5d8a73] border-[#5d8a73] text-white" : "border-slate-300"}`}>{on && <Check size={13} />}</span>
                  <span className="text-sm text-slate-700">{tg.text}<span className="text-xs text-slate-400 ml-2">{tg.subject?.replace("_", " ")}</span></span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Agenda / action checklist */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mt-4">
        <h3 className="font-['Outfit'] font-bold text-slate-800 mb-3">Agenda &amp; action items</h3>
        <div className="space-y-2" data-testid="meeting-checklist">
          {(m.checklist || []).map((c) => (
            <div key={c._k} className="flex items-center gap-3 group">
              <button data-testid={`checklist-toggle-${c._k}`} onClick={() => toggleItem(c._k)}
                className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${c.done ? "bg-[#5d8a73] border-[#5d8a73] text-white" : "border-slate-300"}`}>{c.done && <Check size={13} />}</button>
              <span className={`flex-1 text-sm ${c.done ? "line-through text-slate-400" : "text-slate-700"}`}>{c.text}</span>
              <button onClick={() => delItem(c._k)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
            </div>
          ))}
          {(m.checklist || []).length === 0 && <p className="text-sm text-slate-400">No items yet. Add one or use "Draft agenda".</p>}
        </div>
        <div className="flex gap-2 mt-3">
          <input data-testid="checklist-input" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Add an agenda or action item…"
            className="flex-1 bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#6C8EBF] focus:bg-white" />
          <button data-testid="add-checklist-btn" onClick={addItem} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl px-3 py-2"><Plus size={16} /></button>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mt-4 mb-10">
        <h3 className="font-['Outfit'] font-bold text-slate-800 mb-2">Notes</h3>
        <textarea data-testid="meeting-notes" rows={4} value={m.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Discussion notes, decisions, follow-ups…"
          className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#6C8EBF] focus:bg-white resize-y" />
        <button data-testid="meeting-delete-btn" onClick={remove} className="no-print mt-4 text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1.5"><Trash2 size={14} /> Delete meeting</button>
      </div>
      </div>
    </div>
  );
}
